<template>
  <section class="working-area-container">
    <WorkingAreaModal
      v-if="isopenWorkingAreaModal"
      @closeModal="closeWorkingAreaModal"
      :workingAreaProp="workingArea"
      :workingAreasServiks="workingAreasServiks"
      @submit="submitWorkingArea"
    />
    <WorkingAreaBulkEditModal
      v-if="isBulkEditOpen"
      :selected-ids="bulkEditIds"
      :site-id="filterSelected.site"
      @close="closeBulkEdit"
      @applied="onBulkEditApplied"
      @error="onBulkEditError"
    />
    <transition v-if="routeName === 'workingArea'" name="fade">
      <div class="custom-button-container">
        <Button
          @click="openWorkingAreaModal"
          v-if="loggedUser.type ==='admin'"
          skin="primary"
        >{{$t('new_area')}}</Button>
      </div>
    </transition>
    <Tabs :isRTL="isRTL" >
      <Tab :label="$t('working_area')" to="/real-time/working-area">
        <template v-if="filterSelected && filterSelected.company && filterSelected.site">
          <Loading v-if="isLoading || !workingAreas" />
          <template v-else>
            <div
              v-if="supervisorRoomCoverage"
              class="wa-site-coverage"
              :class="{
                'wa-site-coverage--ok': supervisorRoomCoverage.isFullyCovered,
                'wa-site-coverage--gap': !supervisorRoomCoverage.isFullyCovered,
              }"
              role="button"
              tabindex="0"
              @click="showSupervisorCoverageDetails"
              @keydown.enter.prevent="showSupervisorCoverageDetails"
            >
              <span>{{ supervisorCoverageLabel }}</span>
              <span v-if="!supervisorRoomCoverage.isFullyCovered" class="wa-site-coverage__hint">
                {{ $t('tap_for_details') }}
              </span>
            </div>
            <WorkingAreaList
              ref="workingAreaList"
              @editWorkingArea="editWorkingArea"
              @removeWorkingArea="removeWorkingArea"
              @bulkEdit="openBulkEdit"
              :workingAreas="workingAreas"
            />
          </template>
        </template>
        <h1 v-else>Please select a company and a site</h1>
      </Tab>
    </Tabs>
  </section>
</template>

<script>
import { emptyWorkingArea } from '../consts/consts';
import Tabs from '../../common/components/Tabs/Tabs';
import Tab from '../../common/components/Tabs/Tab';
import Button from '../../common/components/Button';
import Loading from '../../common/components/Loading';
import WorkingAreaList from '../components/WorkingAreaList';
import WorkingAreaModal from '../components/WorkingAreaModal';
import WorkingAreaBulkEditModal from '../components/WorkingAreaBulkEditModal.vue';
import workingAreaService from '../services/workingAreaService';
import roomService from '../../control-panel/services/roomService';
import swalService from '@/modules/common/services/swalService'
import { mapGetters } from 'vuex';

const ACTIVE_COVERAGE_POLL_MS = 8000

export default {
  data() {
    return {
      workingAreas: null,
      workingArea: null,
      workingAreasServiks: [],
      isopenWorkingAreaModal: false,
      isLoading: false,
      activeCoverageTimer: null,
      isBulkEditOpen: false,
      bulkEditIds: [],
      supervisorRoomCoverage: null,
    }
  },
  methods: {
    openBulkEdit(selectedIds) {
      this.bulkEditIds = [...(selectedIds || [])]
      this.isBulkEditOpen = true
    },
    closeBulkEdit() {
      this.isBulkEditOpen = false
      this.bulkEditIds = []
    },
    async onBulkEditApplied(result) {
      const updated = (result && result.updated) || 0
      const unchanged = (result && result.unchanged) || 0
      const skipped = (result && result.skipped) || 0
      swalService.savedMsg(this.$t('wa_bulk_edit_result', { updated, unchanged, skipped }))
      this.closeBulkEdit()
      if (this.$refs.workingAreaList && this.$refs.workingAreaList.clearSelection) {
        this.$refs.workingAreaList.clearSelection()
      }
      this.workingAreas = await workingAreaService.getWorkingAreas(this.filterSelected)
      await this.loadActiveCoverage()
    },
    onBulkEditError(err) {
      const errorMessage = (err && err.message) || this.$t('Something went wrong!')
      swalService.errorMsg(errorMessage)
    },
    showSupervisorCoverageDetails() {
      const coverage = this.supervisorRoomCoverage
      if (!coverage || coverage.isFullyCovered) return

      const rooms = coverage.uncoveredRooms || []
      const areas = coverage.uncoveredWorkingAreas || []
      let html = ''

      if (rooms.length) {
        const names = rooms.map((room) => room.name).join(', ')
        html += `<strong>${this.$t('uncovered_rooms_title')}</strong><br/>${names}`
        if (coverage.uncoveredCount > rooms.length) {
          html += `<br/>${this.$t('and_n_more', { count: coverage.uncoveredCount - rooms.length })}`
        }
      }

      if (areas.length) {
        if (html) html += '<br/><br/>'
        const areaLines = areas
          .map((area) => `${area.name} (${area.activeCount}/${area.assignedCount})`)
          .join('<br/>')
        html += `<strong>${this.$t('uncovered_working_areas_title')}</strong><br/>${areaLines}`
      }

      swalService.showAssignmentIssues({
        title: this.$t('site_supervisor_coverage_title'),
        html: html || this.$t('site_supervisor_coverage_issues', { count: coverage.uncoveredCount }),
        confirmButtonText: this.$t('OK'),
      })
    },
    async submitWorkingArea(newWorkingArea) {
      try {
        const site_id = this.filterSelected.site;
        const res = await workingAreaService.save(newWorkingArea, site_id)
        if (res) {
          swalService.savedMsg(this.$t('Your work has been saved'))
          this.closeWorkingAreaModal()
          this.workingAreas = await workingAreaService.getWorkingAreas(this.filterSelected);
          await this.loadActiveCoverage()
        } else console.log('ERROR WHILE SAVING WORKING')
      } catch (err) {
        const errorMessage = err.message || this.$t('Something went wrong!')
        swalService.errorMsg(errorMessage)
      }
    },
    async editWorkingArea(workingAreaId) {
      const workingAreaToEdit = await workingAreaService.getById(workingAreaId)
      this.openWorkingAreaModal(workingAreaToEdit)
    },
    async removeWorkingArea(workingAreaId) {
      const confirm = await swalService.confirmMsg()

      if (!confirm.value) return
      try {
        await workingAreaService.remove(workingAreaId)
        swalService.savedMsg(this.$t('Your work has been saved'))
        this.workingAreas = await workingAreaService.getWorkingAreas(this.filterSelected);
        await this.loadActiveCoverage()
      } catch (err) {
        await swalService.errorMsg()
      }
    },
    closeWorkingAreaModal() {
      this.isopenWorkingAreaModal = false;
      this.workingArea = JSON.parse(JSON.stringify(emptyWorkingArea))
    },
    async openWorkingAreaModal(workingAreaToEdit) {
      // Check if this is a new working area (no id or undefined)
      const isNewWorkingArea = !workingAreaToEdit || !workingAreaToEdit.id
      
      if (isNewWorkingArea) {
        // For new working area, check if rooms exist
        if (!this.filterSelected.site) {
          swalService.errorMsg(this.$t('please_select_site_first'))
          return
        }
        const rooms = await roomService.getRooms(this.filterSelected)
        if (!rooms || rooms.length === 0) {
          swalService.errorMsg(this.$t('please_create_room_before_working_area'))
          return
        }
      }

      this.workingArea = workingAreaToEdit && workingAreaToEdit.id ? workingAreaToEdit : JSON.parse(JSON.stringify(emptyWorkingArea))
      if (workingAreaToEdit && workingAreaToEdit.id) {
        if (workingAreaToEdit.rooms.length > 0) {
          const roomsIds = workingAreaToEdit.rooms.map(room => room.id)
          const { active_notification_task } = workingAreaToEdit
          const roomsServiks = await workingAreaService.getServiksByRoomsIds(roomsIds)
          // Already selected serviks + all rooms serviks + default selected working area serviks
          const combinedServiks = [...roomsServiks, ...active_notification_task, ...this.workingAreasServiks]
          // Remove duplicates using Set and set the combined serviks as select options
          this.workingAreasServiks = [...new Set(combinedServiks.map(item => item.id))]
            .map(id => combinedServiks.find(item => item.id === id))
          }
      } else {
        // new working area
        const defaultServiks = workingAreaService.defaultSelectedWorkingAreaServiks()
        this.workingAreasServiks = [...defaultServiks]
        this.workingArea.active_notification_task = [...defaultServiks]
      }
      this.isopenWorkingAreaModal = true;
    },
    async loadActiveCoverage() {
      // Refresh supervisor coverage used by the Status pill (active = ≥1 supervisor on).
      if (!this.filterSelected.site || !this.workingAreas) return
      try {
        const [coverageMap, roomCoverage] = await Promise.all([
          workingAreaService.getSiteActiveCoverage(this.filterSelected.site),
          workingAreaService.getSiteSupervisorRoomCoverage(this.filterSelected.site),
        ])
        const enriched = this.workingAreas.map((w) => {
          const coverage = coverageMap[w.id]
          if (!coverage) {
            return { ...w, active_supervisors: [], active_supervisors_count: 0, assigned_supervisors_count: 0 }
          }
          return {
            ...w,
            active_supervisors: coverage.activeSupervisors,
            active_supervisors_count: coverage.activeCount,
            assigned_supervisors_count: coverage.assignedCount,
          }
        })
        this.workingAreas = enriched
        this.supervisorRoomCoverage = roomCoverage || null
      } catch (err) {
        console.warn('Could not load site active coverage', err)
      }
    },
    _startActiveCoveragePolling() {
      this._stopActiveCoveragePolling()
      this.activeCoverageTimer = setInterval(() => {
        this.loadActiveCoverage()
      }, ACTIVE_COVERAGE_POLL_MS)
    },
    _stopActiveCoveragePolling() {
      if (this.activeCoverageTimer) {
        clearInterval(this.activeCoverageTimer)
        this.activeCoverageTimer = null
      }
    },
    _handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return
      if (!this.filterSelected.site || !this.workingAreas) return
      this.loadActiveCoverage()
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this._handleVisibilityChange)
  },
  watch: {
    '$store.getters.filterSelected.site': {
      async handler() {
        this._stopActiveCoveragePolling()
        this.supervisorRoomCoverage = null
        if (!this.filterSelected.site) return;
        this.isLoading = true;
        this.workingAreas = await workingAreaService.getWorkingAreas(this.filterSelected);
        this.workingAreasServiks = workingAreaService.defaultSelectedWorkingAreaServiks()
        this.isLoading = false;
        await this.loadActiveCoverage()
        this._startActiveCoveragePolling()
      },
      immediate: true
    },
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this._handleVisibilityChange)
    this._stopActiveCoveragePolling()
  },
  computed: {
    ...mapGetters(['filterSelected','loggedUser','isRTL']),
    routeName() {
      return this.$route.name;
    },
    supervisorCoverageLabel() {
      const coverage = this.supervisorRoomCoverage
      if (!coverage) return ''
      if (coverage.isFullyCovered) {
        return this.$t('site_supervisor_coverage_ok', {
          covered: coverage.coveredCount,
          total: coverage.totalRooms,
        })
      }
      return this.$t('site_supervisor_coverage_issues', { count: coverage.uncoveredCount })
    },
  },
  components: { Tabs, Tab, Button, Loading, WorkingAreaList, WorkingAreaModal, WorkingAreaBulkEditModal }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.working-area-container {
  background-color: white;
  min-height: 100%;

  @media (max-width: 768px) {
    :deep(.md-tabs) {
      .md-tabs-navigation {
        padding-inline: 8px;
      }
    }
  }
}

.wa-site-coverage {
  margin: 0 12px 12px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  &--ok {
    background: #ecfdf5;
    color: #065f46;
    cursor: default;
  }

  &--gap {
    background: #fef3c7;
    color: #92400e;
    &:hover {
      background: #fde68a;
    }
  }

  &__hint {
    font-weight: 500;
    opacity: 0.85;
    white-space: nowrap;
  }
}

.custom-button-container {
  height: 80px;
  display: flex;
  align-items: center;
  margin-inline-end: 20px;
  text-transform: uppercase;
  position: absolute;
  z-index: 3;
  right: 0;
  @include rtl {
    left: 0;
    right: unset;
  }

  @media (max-width: 768px) {
    position: static;
    height: auto;
    width: 100%;
    margin: 0;
    padding: 8px 12px 0;
    box-sizing: border-box;
    justify-content: stretch;

    button {
      width: 100%;
      min-height: 40px;
      font-size: 13px;
    }
  }

  span {
    @include modal-input-label;
    margin-bottom: 0px;
    font-size: 13px;
  }
  input {
    @include modal-input;
    margin-inline-start: 5px;
    margin-bottom: 0px;
    padding: 0 5px;
    width: 150px;
  }
  button {
    font-size: 12px;
    font-weight: bold;
  }
}
</style>
